/**
 * The public page about the phone app (/app): what it does and where to get it. Both apps are in
 * the stores now, so the page hands out the two store buttons (and a QR code for a laptop) instead
 * of the sideload APK it used to; it is still where the app sends a phone on an old build
 * (MobileApp APP_PAGE_URL).
 *
 * Copy lives in appConstants.ts and is limited to what the app actually does. Styled with the
 * shared brand CSS so it reads as part of the product, next to the flagship, plugin and contact
 * pages.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { EMO_EMAIL } from "../constants";
import { ROUTES } from "../../constants/routes";
import { brandPageCss } from "../brand/brandPageCss";
import { BrandNav, BrandFooter, useBrandTheme } from "../brand/BrandChrome";
import { APP_STORE_URL, PLAY_STORE_URL, storeLink } from "../pages/homepage/constants/homeLayoutsText";
import { QR_PATH, QR_SIZE } from "../pages/homepage/components/home3/filmData";
import {
  APP_EYEBROW,
  APP_TITLE,
  APP_SUBTITLE,
  APP_STORE_FINE,
  APP_QR_LABEL,
  APP_DOES_EYEBROW,
  APP_DOES_HEADING,
  APP_DOES,
  APP_AI_EYEBROW,
  APP_AI_HEADING,
  APP_AI,
  APP_KNOW_HEADING,
  APP_KNOW,
  APP_HELP_PREFIX,
} from "./appConstants";

const SCOPE = "ccap";

const AppDownloadPage: React.FC = () => {
  const navigate = useNavigate();
  const [theme, toggleTheme] = useBrandTheme();

  return (
    <div className={SCOPE} data-theme={theme}>
      <style>{brandPageCss(SCOPE) + PAGE_CSS}</style>

      <BrandNav theme={theme} onToggleTheme={toggleTheme}>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.PLUGIN)}>
          Premiere plugin
        </button>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.PRICING)}>
          Pricing
        </button>
        <button className="linkbtn hide-sm" onClick={() => navigate(ROUTES.CONTACT)}>
          Help
        </button>
      </BrandNav>

      <div className="wrap hero">
        <span className="eyebrow">{APP_EYEBROW}</span>
        <h1>{APP_TITLE}</h1>
        <p className="sub">{APP_SUBTITLE}</p>

        <div className="cta stores">
          <a href={storeLink(APP_STORE_URL, "app-page")} target="_blank" rel="noopener noreferrer">
            <img className="as" src={theme === "dark" ? "/app/appstore-badge-white.svg" : "/app/appstore-badge-black.svg"} alt="Download on the App Store" />
          </a>
          <a href={storeLink(PLAY_STORE_URL, "app-page")} target="_blank" rel="noopener noreferrer">
            <img className="gp" src="/app/googleplay-badge.png" alt="Get it on Google Play" />
          </a>
          <span className="qr">
            <svg viewBox={`-2 -2 ${QR_SIZE + 4} ${QR_SIZE + 4}`} role="img" aria-label={APP_QR_LABEL}>
              <rect x="-2" y="-2" width={QR_SIZE + 4} height={QR_SIZE + 4} fill="#fff" />
              <path d={QR_PATH} stroke="#17110f" fill="none" />
            </svg>
            {APP_QR_LABEL}
          </span>
        </div>
        <p className="fine">{APP_STORE_FINE}</p>
      </div>

      <section className="wrap">
        <div className="sec-head">
          <span className="eyebrow">{APP_DOES_EYEBROW}</span>
          <h2>{APP_DOES_HEADING}</h2>
        </div>
        <ul className="uses">
          {APP_DOES.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="wrap">
        <div className="sec-head">
          <span className="eyebrow">{APP_AI_EYEBROW}</span>
          <h2>{APP_AI_HEADING}</h2>
        </div>
        <ul className="uses">
          {APP_AI.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="wrap">
        <div className="sec-head">
          <h2>{APP_KNOW_HEADING}</h2>
        </div>
        <ul className="uses">
          {APP_KNOW.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <BrandFooter>
        <span>
          {APP_HELP_PREFIX} <a href={`mailto:${EMO_EMAIL}`}>{EMO_EMAIL}</a>
        </span>
        <button className="linkbtn" onClick={() => navigate(ROUTES.PRIVACY)}>
          Privacy
        </button>
        <button className="linkbtn" onClick={() => navigate(ROUTES.TERMS)}>
          Terms
        </button>
      </BrandFooter>
    </div>
  );
};

// Same step / list primitives as the plugin download page, scoped to this page.
const PAGE_CSS = `
.ccap .stores{display:flex;flex-wrap:wrap;align-items:center;gap:14px}
.ccap .stores a{display:block}
.ccap .stores img.as{height:48px;display:block}
.ccap .stores img.gp{height:70px;margin:-11px -10px;display:block}
.ccap .stores .qr{display:inline-flex;align-items:center;gap:12px;margin-left:10px;font-size:14px;color:var(--muted)}
.ccap .stores .qr svg{width:84px;height:84px;border-radius:10px;box-shadow:0 0 0 1px var(--line)}
@media (max-width:700px){.ccap .stores .qr{display:none}}
.ccap .steps{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:2px}
.ccap .step{display:flex;gap:20px;padding:22px 0;border-top:1px solid var(--line)}
.ccap .step:first-child{border-top:none;padding-top:0}
.ccap .step .k{flex:none;font-size:13px;font-weight:700;letter-spacing:.1em;color:var(--cherry);padding-top:3px;width:26px}
.ccap .step-body{min-width:0}
.ccap .step h3{font-size:18px;margin-bottom:6px}
.ccap .step p{margin:0;color:var(--muted);font-size:14.5px}
.ccap .uses{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:14px}
.ccap .uses li{position:relative;padding-left:26px;color:var(--muted);font-size:15px}
.ccap .uses li::before{content:"";position:absolute;left:6px;top:9px;width:7px;height:7px;border-radius:50%;background:var(--cherry)}
@media (max-width:560px){ .ccap .step{gap:14px} }
`;

export default AppDownloadPage;
